import type { TrainingProfile } from '../schema/profile';

const t = (cs: string, en: string) => ({ 'cs-CZ': cs, 'en-US': en });

/**
 * V1 DRAFT - authored for structural completeness; NOT a substitute for sign-off
 * by a Czech osoba odborně způsobilá v prevenci rizik. Legal refs per the Czech
 * BOZP research.
 *
 * Ruční manipulace s břemeny. Czech-only (LocalizedText cs-CZ). 2 chapters
 * (hmotnostní limity, technika zvedání) + a graded quiz with one question per
 * kind. Answer keys live here (repo-privileged) and in the DB; stripped from the
 * client payload by the API.
 */
export const manualHandlingProfile: TrainingProfile = {
  slug: 'manual-handling',
  // Top-level title is a PLAIN STRING - seeded verbatim into the varchar
  // bozp_hazard_profiles.title column. Chapter titles below stay LocalizedText.
  title: 'Ruční manipulace s břemeny',
  passThreshold: 80,
  content: {
    schemaVersion: 1,
    chapters: [
      {
        id: 'ch-hmotnostni-limity',
        title: t('Hmotnostní limity', 'Weight limits'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['kc-limity'],
        blocks: [
          {
            id: 'h-limity',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Ruční manipulace s břemeny', 'Manual handling of loads'),
          },
          {
            id: 'p-limity-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Ruční manipulací s břemenem se rozumí **přepravování nebo nošení břemene** jedním či více zaměstnanci, včetně **zvedání, pokládání, strkání, tahání, posunování nebo přemísťování**, při kterém v důsledku vlastností břemene nebo nepříznivých podmínek hrozí poškození zejména **páteře a pohybového aparátu**.',
              'Manual handling of a load means **transporting or carrying a load** by one or more employees, including **lifting, putting down, pushing, pulling, moving or shifting** it, where, owing to the properties of the load or unfavourable conditions, there is a risk of harm particularly to the **spine and musculoskeletal system**.',
            ),
          },
          {
            id: 'c-limity-pravo',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'info',
            markdown: t(
              'Právní základ: **NV 361/2007 Sb., §29** a **příloha č. 5** - hygienické limity pro ruční manipulaci s břemeny a hodnocení zdravotního rizika fyzické zátěže.',
              'Legal basis: **Gov. Reg. 361/2007 Coll., §29** and **Annex No. 5** - hygiene limits for the manual handling of loads and the assessment of the health risk from physical strain.',
            ),
          },
          {
            id: 'p-limity-2',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Limity rozlišují **pohlaví**, **četnost** manipulace (občasná vs. častá) a **kumulativní hmotnost** přenesenou za směnu. Orientačně se uvádí přípustná hmotnost ručně zvedaného a přenášeného břemene u mužů **50 kg** při občasné a **30 kg** při časté manipulaci, u žen **20 kg** při občasné a **15 kg** při časté manipulaci.',
              'The limits distinguish **sex**, the **frequency** of handling (occasional vs. frequent) and the **cumulative weight** carried per shift. As a guide, the permissible weight of a manually lifted and carried load is **50 kg** for men in occasional and **30 kg** in frequent handling, and **20 kg** for women in occasional and **15 kg** in frequent handling.',
            ),
          },
          {
            id: 'cl-limity',
            blockType: 'checklist',
            schemaVersion: 1,
            items: [
              t(
                'Zjisti hmotnost a těžiště břemene před zvedáním',
                'Determine the weight and centre of gravity of the load before lifting',
              ),
              t(
                'Respektuj limit podle pohlaví a četnosti manipulace',
                'Respect the limit according to sex and frequency of handling',
              ),
              t(
                'U těžkých nebo neskladných břemen použij pomůcku nebo druhou osobu',
                'For heavy or awkward loads, use an aid or a second person',
              ),
              t(
                'Sleduj kumulativní hmotnost přenesenou za směnu',
                'Track the cumulative weight carried per shift',
              ),
            ],
          },
          {
            id: 'c-limity-warn',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'warning',
            markdown: t(
              'Pro **mladistvé** a **těhotné zaměstnankyně** platí výrazně **nižší limity**. Tyto skupiny nesmí být zařazeny k práci překračující jejich snížené hodnoty.',
              'For **young workers** and **pregnant employees**, significantly **lower limits** apply. These groups must not be assigned work that exceeds their reduced values.',
            ),
          },
          {
            id: 'kc-limity',
            blockType: 'knowledgeCheck',
            schemaVersion: 1,
            prompt: t(
              'Na čem závisí přípustný hmotnostní limit ručně přenášeného břemene?',
              'What does the permissible weight limit of a manually carried load depend on?',
            ),
            choices: [
              {
                id: 'kc-a',
                label: t(
                  'Na pohlaví a četnosti manipulace',
                  'On sex and the frequency of handling',
                ),
              },
              { id: 'kc-b', label: t('Pouze na výšce pracovníka', "On the worker's height alone") },
            ],
            correctChoiceId: 'kc-a',
            explanation: t(
              'Limity dle NV 361/2007 Sb. rozlišují pohlaví, četnost manipulace i kumulativní hmotnost za směnu.',
              'The limits under Gov. Reg. 361/2007 Coll. distinguish sex, the frequency of handling, and the cumulative weight per shift.',
            ),
          },
          {
            id: 'k-limity',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Hmotnostní limity nejsou jen čísla - chrání **páteř a pohybový aparát**. U mužů občasně **50 kg**, u žen občasně **20 kg**.',
              'Weight limits are not just numbers - they protect the **spine and musculoskeletal system**. For men occasionally **50 kg**, for women occasionally **20 kg**.',
            ),
          },
        ],
      },
      {
        id: 'ch-technika-zvedani',
        title: t('Technika zvedání', 'Lifting technique'),
        minDwellSeconds: 20,
        requiredWidgetIds: ['ord-technika'],
        blocks: [
          {
            id: 'h-technika',
            blockType: 'heading',
            schemaVersion: 1,
            level: 2,
            text: t('Bezpečná technika zvedání', 'Safe lifting technique'),
          },
          {
            id: 'p-technika-1',
            blockType: 'paragraph',
            schemaVersion: 1,
            markdown: t(
              'Zvedej **nohama, ne zády**. Při zvedání drž **rovná záda**, pokrč kolena, břemeno přitáhni co nejblíže k tělu a zvedej plynule silou nohou. **Vyhni se současnému zvedání a otáčení trupu** - při změně směru přešlapuj nohama.',
              'Lift **with your legs, not your back**. When lifting, keep your **back straight**, bend your knees, draw the load as close to your body as possible, and lift smoothly using the power of your legs. **Avoid lifting and twisting your torso at the same time** - when changing direction, step around with your feet.',
            ),
          },
          {
            id: 'anim-zvedani',
            blockType: 'animation',
            schemaVersion: 1,
            scene: 'liftTechnique',
            steps: [
              {
                label: t('Shrbená záda', 'Rounded back'),
                say: t('Ohnutá páteř nese celou zátěž.', 'A bent spine carries the whole load.'),
              },
              {
                label: t('Rovná záda, síla z nohou', 'Straight back, legs do the work'),
                say: t(
                  'Břemeno u těla, pokrčená kolena, zvedej nohama.',
                  'Load close to the body, knees bent, lift with the legs.',
                ),
              },
            ],
            caption: t(
              'Rovná záda a síla z nohou - shrbené zvedání poškozuje páteř.',
              'Straight back and power from the legs - hunched lifting damages the spine.',
            ),
          },
          {
            id: 'ord-technika',
            blockType: 'ordering',
            schemaVersion: 1,
            prompt: t(
              'Seřaď kroky správné techniky zvedání břemene do správného pořadí.',
              'Put the steps of the correct load-lifting technique into the right order.',
            ),
            items: [
              {
                id: 'op-1',
                label: t(
                  'Postav se blízko břemene, nohy na šíři ramen',
                  'Stand close to the load, feet shoulder-width apart',
                ),
              },
              {
                id: 'op-2',
                label: t(
                  'Pokrč kolena a udrž rovná záda',
                  'Bend your knees and keep your back straight',
                ),
              },
              {
                id: 'op-3',
                label: t(
                  'Uchop břemeno pevně a přitáhni k tělu',
                  'Grip the load firmly and draw it to your body',
                ),
              },
              {
                id: 'op-4',
                label: t(
                  'Zvedni plynule silou nohou bez otáčení trupu',
                  'Lift smoothly with the power of your legs, without twisting your torso',
                ),
              },
            ],
          },
          {
            id: 'c-technika-danger',
            blockType: 'callout',
            schemaVersion: 1,
            variant: 'danger',
            markdown: t(
              '**Nikdy nezvedej těžké břemeno s ohnutými zády a nataženýma nohama** ani je nezvedej trhnutím - hrozí akutní poranění bederní páteře a meziobratlových plotének.',
              '**Never lift a heavy load with a bent back and straight legs**, and never lift it with a jerk - you risk acute injury to the lumbar spine and the intervertebral discs.',
            ),
          },
          {
            id: 'acc-technika',
            blockType: 'accordion',
            schemaVersion: 1,
            items: [
              {
                q: t(
                  'Co dělat s neskladným nebo příliš těžkým břemenem?',
                  'What should you do with an awkward or too-heavy load?',
                ),
                a: t(
                  'Rozděl je na menší části, použij manipulační pomůcku (vozík, pásy) nebo požádej o pomoc druhou osobu a manipulaci si předem domluv.',
                  'Split it into smaller parts, use a handling aid (a cart, straps), or ask a second person for help and agree on the handling beforehand.',
                ),
              },
              {
                q: t(
                  'Jak se vyhnout poranění při změně směru?',
                  'How do you avoid injury when changing direction?',
                ),
                a: t(
                  'Neotáčej trup se zatíženou páteří. Otoč se přešlapováním celých nohou tak, aby ramena, pánev a špičky směřovaly stejným směrem.',
                  'Do not twist your torso while your spine is loaded. Turn by stepping around with your whole feet so that your shoulders, pelvis and toes point in the same direction.',
                ),
              },
            ],
          },
          {
            id: 'k-technika',
            blockType: 'keyTakeaway',
            schemaVersion: 1,
            markdown: t(
              'Zvedej **nohama, ne zády**, břemeno drž **u těla** a **neotáčej** se se zatíženou páteří.',
              'Lift **with your legs, not your back**, keep the load **close to your body**, and **do not twist** while your spine is loaded.',
            ),
          },
        ],
      },
    ],
  },
  questions: [
    {
      id: 'q-limit-muz',
      kind: 'numeric',
      chapterId: 'ch-hmotnostni-limity',
      prompt: t(
        'Jaká je orientační nejvyšší přípustná hmotnost (v kg) ručně zvedaného a přenášeného břemene u muže při občasné manipulaci?',
        'What is the approximate maximum permissible weight (in kg) of a manually lifted and carried load for a man in occasional handling?',
      ),
      correct: 50,
      tolerance: 0,
    },
    {
      id: 'q-zavislost',
      kind: 'single',
      chapterId: 'ch-hmotnostni-limity',
      prompt: t(
        'Na čem závisí přípustný hmotnostní limit ručně přenášeného břemene?',
        'What does the permissible weight limit of a manually carried load depend on?',
      ),
      choices: [
        { id: 'a', label: t('Pouze na hmotnosti pracovníka', "On the worker's weight alone") },
        {
          id: 'b',
          label: t('Na pohlaví a četnosti manipulace', 'On sex and the frequency of handling'),
        },
        { id: 'c', label: t('Na barvě obalu břemene', "On the colour of the load's packaging") },
      ],
      correct: 'b',
    },
    {
      id: 'q-mladistvi-tf',
      kind: 'truefalse',
      chapterId: 'ch-hmotnostni-limity',
      prompt: t(
        'Pro mladistvé a těhotné zaměstnankyně platí nižší hmotnostní limity.',
        'Lower weight limits apply to young workers and pregnant employees.',
      ),
      correct: true,
    },
    {
      id: 'q-technika-multi',
      kind: 'multi',
      chapterId: 'ch-technika-zvedani',
      prompt: t(
        'Které zásady patří k bezpečné technice zvedání? (vyber všechny správné)',
        'Which principles belong to safe lifting technique? (select all that apply)',
      ),
      choices: [
        {
          id: 'a',
          label: t(
            'Pokrčit kolena a držet rovná záda',
            'Bend the knees and keep the back straight',
          ),
        },
        {
          id: 'b',
          label: t('Přitáhnout břemeno blízko k tělu', 'Draw the load close to the body'),
        },
        {
          id: 'c',
          label: t('Současně zvedat a otáčet trup', 'Lift and twist the torso at the same time'),
        },
        {
          id: 'd',
          label: t('Zvedat plynule silou nohou', 'Lift smoothly with the power of the legs'),
        },
      ],
      correct: ['a', 'b', 'd'],
    },
    {
      id: 'q-technika-order',
      kind: 'ordering',
      chapterId: 'ch-technika-zvedani',
      prompt: t(
        'Seřaď kroky správné techniky zvedání břemene.',
        'Order the steps of the correct load-lifting technique.',
      ),
      choices: [
        { id: 'a', label: t('Postavit se blízko břemene', 'Stand close to the load') },
        { id: 'b', label: t('Pokrčit kolena, rovná záda', 'Bend the knees, straight back') },
        { id: 'c', label: t('Uchopit a přitáhnout k tělu', 'Grip and draw to the body') },
        { id: 'd', label: t('Zvednout silou nohou', 'Lift with the power of the legs') },
      ],
      correct: ['a', 'b', 'c', 'd'],
    },
  ],
};
